import { Ban, Bot, Loader2, Terminal, UserCog } from "lucide-react";
import { useLanguage } from "@/hooks/use-language";
import type { AdminUser } from "@/lib/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface SuspendedUsersPanelProps {
  users: AdminUser[];
  loading: boolean;
  /** Opens the shared confirm dialog in the admin page. */
  onReinstate: (user: AdminUser) => void;
}

function formatDate(iso: string, locale: string) {
  return new Date(iso).toLocaleString(locale === "ko" ? "ko-KR" : "en-US", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function SuspendedUsersPanel({ users, loading, onReinstate }: SuspendedUsersPanelProps) {
  const { t, locale } = useLanguage();

  // Newest suspension first; entries without a timestamp (suspended before
  // details were recorded) go last.
  const suspended = users
    .filter((u) => u.suspended)
    .sort((a, b) => (b.suspended_at ?? "").localeCompare(a.suspended_at ?? ""));

  function byLabel(by?: string) {
    if (by === "auto")
      return (
        <Badge variant="destructive" className="gap-1">
          <Bot className="size-3" />
          {t("admin_block.suspended_by_auto")}
        </Badge>
      );
    if (by === "cli")
      return (
        <Badge variant="secondary" className="gap-1">
          <Terminal className="size-3" />
          {t("admin_block.suspended_by_cli")}
        </Badge>
      );
    if (by)
      return (
        <Badge variant="secondary" className="gap-1">
          <UserCog className="size-3" />
          {by}
        </Badge>
      );
    return null;
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t("admin_block.suspended_title")}</h2>
        <p className="text-sm text-muted-foreground">{t("admin_block.suspended_desc")}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        </div>
      ) : suspended.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
          {t("admin_block.suspended_empty")}
        </div>
      ) : (
        <div className="space-y-2">
          {suspended.map((u) => (
            <Card key={u.id}>
              <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Ban className="size-4 shrink-0 text-destructive" />
                    <span className="break-all text-sm font-semibold">{u.email}</span>
                    {byLabel(u.suspended_by)}
                  </div>
                  <p className="text-sm break-words">
                    {u.suspend_reason || (
                      <span className="text-muted-foreground">{t("admin_block.no_reason")}</span>
                    )}
                  </p>
                  {u.suspended_at && (
                    <p className="text-xs text-muted-foreground">
                      {t("admin_block.suspended_at")}: {formatDate(u.suspended_at, locale)}
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0 cursor-pointer"
                  onClick={() => onReinstate(u)}
                >
                  {t("admin_block.unsuspend")}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
